import React, { useCallback, useEffect, useState } from 'react'
import { API_BASE_URL } from '../data'
import { AllocationStatusItem, AllocationStatusPageMetaProps, Area, Event, ShiftWithDuty } from '../data/types'
import { WarningIcon, XIcon } from '../icons'
import { Button } from './ui'
import { getShiftDisplayName, sortByName, sortShifts } from '../utils/sorting'

type Props = {
    meta: AllocationStatusPageMetaProps
    isOpen: boolean
    onClose: () => void
    selectedIds: number[]
    allocations: AllocationStatusItem[]
    onSuccess: () => void
}

type DuplicateInfo = {
    volunteer_id: number
    volunteer_name: string
    has_duplicate: boolean
    existing_status?: string
}

export const ReAllocateModal = (props: Props) => {
    const { meta, isOpen, onClose, selectedIds, allocations, onSuccess } = props

    const [events, setEvents] = useState<Event[]>([])
    const [areas, setAreas] = useState<Area[]>([])
    const [shifts, setShifts] = useState<ShiftWithDuty[]>([])

    const [selectedEventId, setSelectedEventId] = useState<number | null>(null)
    const [selectedAreaId, setSelectedAreaId] = useState<number | null>(null)
    const [selectedShiftId, setSelectedShiftId] = useState<number | null>(null)
    const [selectedShift, setSelectedShift] = useState<ShiftWithDuty | null>(null)

    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false)
    const [duplicates, setDuplicates] = useState<DuplicateInfo[]>([])
    const [error, setError] = useState<string | null>(null)

    // Get selected allocations details
    const selectedAllocations = allocations.filter(a => selectedIds.includes(a.id))
    const volunteerIds = Array.from(new Set(selectedAllocations.map(a => a.allocation?.volunteer_id).filter(Boolean)))

    // Fetch events - backend handles filtering by lead assignments and reallocation manager assignments
    useEffect(() => {
        if (!isOpen) return

        const fetchEvents = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/event`, { credentials: 'include' })
                if (response.ok) {
                    const result = await response.json()
                    let eventData = result.value || result
                    if (Array.isArray(eventData) && eventData.length > 0 && Array.isArray(eventData[0])) {
                        eventData = eventData[0]
                    }
                    setEvents(Array.isArray(eventData) ? eventData : [])
                }
            } catch (err) {
                console.error('Error fetching events:', err)
            }
        }
        fetchEvents()
    }, [isOpen])

    // Fetch areas when event changes
    useEffect(() => {
        if (selectedEventId) {
            const fetchAreas = async () => {
                try {
                    const response = await fetch(`${API_BASE_URL}/area/${selectedEventId}`, { credentials: 'include' })
                    if (response.ok) {
                        const result = await response.json()
                        // API returns areas wrapped in an array
                        let areaData = result
                        if (Array.isArray(areaData) && areaData.length > 0 && Array.isArray(areaData[0])) {
                            areaData = areaData[0]
                        } else if (Array.isArray(areaData) && areaData.length === 1 && Array.isArray(areaData[0])) {
                            areaData = areaData[0]
                        }
                        setAreas(Array.isArray(areaData) ? areaData : [])
                    }
                } catch (err) {
                    console.error('Error fetching areas:', err)
                }
            }
            fetchAreas()
            setSelectedAreaId(null)
            setSelectedShiftId(null)
            setShifts([])
            setDuplicates([])
        } else {
            setAreas([])
            setSelectedAreaId(null)
            setSelectedShiftId(null)
            setShifts([])
            setDuplicates([])
        }
    }, [selectedEventId])

    // Fetch shifts when area changes
    useEffect(() => {
        if (selectedAreaId) {
            const fetchShifts = async () => {
                try {
                    const response = await fetch(`${API_BASE_URL}/shift/${selectedAreaId}`, { credentials: 'include' })
                    if (response.ok) {
                        const result = await response.json()
                        // API returns shifts wrapped in an array
                        let shiftData = result
                        if (Array.isArray(shiftData) && shiftData.length > 0 && Array.isArray(shiftData[0])) {
                            shiftData = shiftData[0]
                        } else if (Array.isArray(shiftData) && shiftData.length === 1 && Array.isArray(shiftData[0])) {
                            shiftData = shiftData[0]
                        }
                        setShifts(Array.isArray(shiftData) ? shiftData : [])
                    }
                } catch (err) {
                    console.error('Error fetching shifts:', err)
                }
            }
            fetchShifts()
            setSelectedShiftId(null)
            setDuplicates([])
        } else {
            setShifts([])
            setSelectedShiftId(null)
            setDuplicates([])
        }
    }, [selectedAreaId])

    // Update selected shift and check for duplicates when shift changes
    useEffect(() => {
        if (selectedShiftId) {
            const shift = shifts.find(s => s.id === selectedShiftId)
            setSelectedShift(shift || null)
            checkDuplicates(selectedShiftId)
        } else {
            setSelectedShift(null)
            setDuplicates([])
        }
    }, [selectedShiftId, shifts])

    const checkDuplicates = useCallback(async (shiftId: number) => {
        if (volunteerIds.length === 0) return

        setIsCheckingDuplicates(true)
        const duplicateResults: DuplicateInfo[] = []

        for (const volunteerId of volunteerIds) {
            try {
                const response = await fetch(`${API_BASE_URL}/allocation/check-duplicate`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({
                        shift_id: shiftId,
                        volunteer_id: volunteerId
                    })
                })

                if (response.ok) {
                    const result = await response.json()
                    const allocation = selectedAllocations.find(a => a.allocation?.volunteer_id === volunteerId)
                    const volunteer = allocation?.allocation?.volunteer

                    duplicateResults.push({
                        volunteer_id: volunteerId,
                        volunteer_name: volunteer ? `${volunteer.first_name} ${volunteer.last_name}` : `Volunteer ${volunteerId}`,
                        has_duplicate: result.has_duplicate,
                        existing_status: result.existing_allocation?.status
                    })
                }
            } catch (err) {
                console.error('Error checking duplicate:', err)
            }
        }

        setDuplicates(duplicateResults)
        setIsCheckingDuplicates(false)
    }, [volunteerIds, selectedAllocations])

    // Format time to H:i format (strip seconds if present)
    const formatTimeForApi = (time: string): string => {
        const parts = time.split(':')
        return `${parts[0]}:${parts[1]}`
    }

    const handleSubmit = async () => {
        if (!selectedShiftId || !selectedShift) return

        setIsSubmitting(true)
        setError(null)

        try {
            // Create new allocations for each volunteer
            const response = await fetch(`${API_BASE_URL}/allocation/shift/${selectedShiftId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    volunteer: volunteerIds,
                    start_time: formatTimeForApi(selectedShift.start_time),
                    end_time: formatTimeForApi(selectedShift.end_time)
                })
            })

            if (!response.ok) {
                throw new Error('Failed to create allocations')
            }

            onSuccess()
            onClose()
        } catch (err) {
            setError(meta.reAllocateErrorMessage)
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleClose = () => {
        setSelectedEventId(null)
        setSelectedAreaId(null)
        setSelectedShiftId(null)
        setSelectedShift(null)
        setAreas([])
        setShifts([])
        setDuplicates([])
        setError(null)
        onClose()
    }

    const hasDuplicates = duplicates.some(d => d.has_duplicate)

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                {/* Backdrop */}
                <div
                    className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75"
                    onClick={handleClose}
                />

                {/* Modal */}
                <div className="inline-block w-full max-w-lg p-6 my-8 overflow-hidden text-start align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-lg">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            {meta.reAllocateTitle}
                        </h3>
                        <button
                            onClick={handleClose}
                            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                        >
                            <XIcon className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Description */}
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        {meta.reAllocateDescription}
                    </p>

                    {/* Selected volunteers summary */}
                    <div className="mb-4 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                            <strong>{volunteerIds.length}</strong> volunteer{volunteerIds.length !== 1 ? 's' : ''} selected
                        </p>
                        <div className="mt-2 flex flex-wrap gap-1">
                            {selectedAllocations.slice(0, 5).map((alloc, idx) => (
                                <span
                                    key={idx}
                                    className="inline-block px-2 py-1 text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded"
                                >
                                    {alloc.allocation?.volunteer?.first_name} {alloc.allocation?.volunteer?.last_name}
                                </span>
                            ))}
                            {selectedAllocations.length > 5 && (
                                <span className="inline-block px-2 py-1 text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded">
                                    +{selectedAllocations.length - 5} more
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Event selection */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {meta.reAllocateEventLabel}
                        </label>
                        <select
                            value={selectedEventId || ''}
                            onChange={(e) => setSelectedEventId(e.target.value ? Number(e.target.value) : null)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                            <option value="">{meta.selectEventPlaceholder}</option>
                            {sortByName(events).map(event => (
                                <option key={event.id} value={event.id}>{event.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Area selection */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {meta.reAllocateAreaLabel}
                        </label>
                        <select
                            value={selectedAreaId || ''}
                            onChange={(e) => setSelectedAreaId(e.target.value ? Number(e.target.value) : null)}
                            disabled={!selectedEventId}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                        >
                            <option value="">{meta.selectAreaPlaceholder}</option>
                            {sortByName(areas).map(area => (
                                <option key={area.id} value={area.id}>{area.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Shift selection */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {meta.reAllocateShiftLabel}
                        </label>
                        <select
                            value={selectedShiftId || ''}
                            onChange={(e) => setSelectedShiftId(e.target.value ? Number(e.target.value) : null)}
                            disabled={!selectedAreaId}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                        >
                            <option value="">{meta.selectShiftPlaceholder}</option>
                            {sortShifts(shifts).map(shift => (
                                <option key={shift.id} value={shift.id}>
                                    {getShiftDisplayName(shift)} ({shift.start_time} - {shift.end_time})
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Warning note */}
                    <div className="mb-4 p-3 bg-warn-50 dark:bg-warn-900/30 border border-warn-200 dark:border-warn-800 rounded-lg">
                        <div className="flex items-start">
                            <WarningIcon className="w-5 h-5 text-warn-500 me-2 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-warn-700 dark:text-warn-300">
                                {meta.reAllocateWarning}
                            </p>
                        </div>
                    </div>

                    {/* Duplicate warning */}
                    {isCheckingDuplicates && (
                        <div className="mb-4 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                Checking for existing allocations...
                            </p>
                        </div>
                    )}

                    {hasDuplicates && !isCheckingDuplicates && (
                        <div className="mb-4 p-3 bg-danger-50 dark:bg-danger-900/30 border border-danger-200 dark:border-danger-800 rounded-lg">
                            <div className="flex items-start">
                                <WarningIcon className="w-5 h-5 text-danger-500 me-2 flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-sm font-medium text-danger-700 dark:text-danger-300 mb-1">
                                        {meta.reAllocateDuplicateWarning}
                                    </p>
                                    <ul className="text-xs text-danger-600 dark:text-danger-400 list-disc list-inside">
                                        {duplicates.filter(d => d.has_duplicate).map((d, idx) => (
                                            <li key={idx}>
                                                {d.volunteer_name} ({d.existing_status})
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Error message */}
                    {error && (
                        <div className="mb-4 p-3 bg-danger-50 dark:bg-danger-900/30 border border-danger-200 dark:border-danger-800 rounded-lg">
                            <p className="text-sm text-danger-700 dark:text-danger-300">{error}</p>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-end gap-3">
                        <Button
                            variant="outline"
                            onClick={handleClose}
                        >
                            {meta.reAllocateCancelButton}
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={!selectedShiftId || isSubmitting || isCheckingDuplicates}
                        >
                            {isSubmitting ? meta.reAllocatingButton : meta.reAllocateSubmitButton}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    )
}
